import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Form, Table } from 'react-bootstrap';
import {
  sprayService,
  SprayCalibration,
  SprayMachine,
  IntensityLevel,
} from '../../services/sprayService';
import CalibrationHistoryModal from './CalibrationHistoryModal';
import { formatDate } from '../../dateFormat';

interface Props {
  calibrations: SprayCalibration[];
  machineId: string;
  onUpdated: (machine: SprayMachine) => void;
  /**
   * Optional handler invoked when the row Edit button is clicked. The
   * parent typically opens CalibrationModal in edit mode. Track Phase 3.
   */
  onEdit?: (calibration: SprayCalibration) => void;
  /**
   * Optional handler invoked when the row Clone button is clicked. The
   * parent typically clones the source values, clears `_id` /
   * `attachmentKey` / `signedOffBy` / `editHistory`, sets date to today,
   * and opens CalibrationModal in Add mode pre-filled. Track Phase 4.
   */
  onClone?: (calibration: SprayCalibration) => void;
}

// Calculated/derived values are displayed to 2 decimal places; user-entered
// values (gear, pressure) are displayed as captured.
const formatDerived = (n?: number): string =>
  n === undefined || n === null ? '' : n.toFixed(2);

const formatIntensity = (lvl: IntensityLevel): string => {
  const parts: string[] = [];
  if (lvl.gear) parts.push(lvl.gear);
  if (lvl.pressure !== undefined && lvl.pressure !== null) parts.push(`${lvl.pressure} bar`);
  if (lvl.litresPerHectare !== undefined && lvl.litresPerHectare !== null) {
    parts.push(`${formatDerived(lvl.litresPerHectare)} L/ha`);
  }
  return parts.length > 0 ? `${lvl.label} (${parts.join(', ')})` : lvl.label;
};

const CalibrationList: React.FC<Props> = ({ calibrations, machineId, onUpdated, onEdit, onClone }) => {
  const { t } = useTranslation();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [historyFor, setHistoryFor] = useState<SprayCalibration | null>(null);
  // Default view shows only the latest calibration. The toggle reveals
  // older ones. Track Phase 4 polish.
  const [showOld, setShowOld] = useState(false);
  // Soft-deleted rows are hidden by default. Track Phase 5.
  const [showDeleted, setShowDeleted] = useState(false);

  const handleAttach = async (calibrationId: string, file: File) => {
    if (file.type !== 'application/pdf') return;
    setBusyId(calibrationId);
    try {
      const updated = await sprayService.uploadCalibrationAttachment(machineId, calibrationId, file);
      onUpdated(updated);
    } finally {
      setBusyId(null);
    }
  };

  const handleReplace = async (calibrationId: string, file: File) => {
    if (file.type !== 'application/pdf') return;
    setBusyId(calibrationId);
    try {
      await sprayService.deleteCalibrationAttachment(machineId, calibrationId);
      const updated = await sprayService.uploadCalibrationAttachment(machineId, calibrationId, file);
      onUpdated(updated);
    } finally {
      setBusyId(null);
    }
  };

  const handleView = async (calibrationId: string) => {
    setBusyId(calibrationId);
    try {
      const url = await sprayService.getCalibrationAttachmentUrl(machineId, calibrationId);
      window.open(url, '_blank');
    } finally {
      setBusyId(null);
    }
  };

  const handleDeletePdf = async (calibrationId: string) => {
    if (!window.confirm(t('spray.machines.calibrations.deleteSignedDocConfirm'))) return;
    setBusyId(calibrationId);
    try {
      const updated = await sprayService.deleteCalibrationAttachment(machineId, calibrationId);
      onUpdated(updated);
    } finally {
      setBusyId(null);
    }
  };

  const handleSoftDelete = async (calibrationId: string) => {
    if (!window.confirm(t('spray.machines.calibrations.deleteCalibrationConfirm'))) return;
    setBusyId(calibrationId);
    try {
      const updated = await sprayService.softDeleteCalibration(machineId, calibrationId);
      onUpdated(updated);
    } finally {
      setBusyId(null);
    }
  };

  const handleRestore = async (calibration: SprayCalibration) => {
    if (!calibration._id) return;
    setBusyId(calibration._id);
    try {
      // Restore via the edit endpoint; we send the calibration's current
      // values plus status='active'. The backend appends an editHistory
      // entry recording the restore.
      const updated = await sprayService.editCalibration(machineId, calibration._id, {
        date: calibration.date,
        performedBy: calibration.performedBy,
        signedOffBy: calibration.signedOffBy,
        treeSpacing: calibration.treeSpacing,
        treesPerHectare: calibration.treesPerHectare,
        intensityLevels: calibration.intensityLevels,
        status: 'active',
      });
      onUpdated(updated);
    } finally {
      setBusyId(null);
    }
  };

  if (calibrations.length === 0) {
    return <p>{t('spray.machines.calibrations.noCalibrations')}</p>;
  }

  // Filter out soft-deleted rows unless the toggle is on. Then sort the
  // active set by date (newest first) and apply the show-old slice.
  const filteredByStatus = showDeleted
    ? calibrations
    : calibrations.filter(c => c.status !== 'deleted');
  const sorted = [...filteredByStatus].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );
  const activeOnly = sorted.filter(c => c.status !== 'deleted');
  const visible = showOld
    ? sorted
    : showDeleted
      ? // When showing deleted, always show the latest active + all deleted
        [...activeOnly.slice(0, 1), ...sorted.filter(c => c.status === 'deleted')]
      : sorted.slice(0, 1);

  const hasMultipleActive = calibrations.filter(c => c.status !== 'deleted').length > 1;
  const hasAnyDeleted = calibrations.some(c => c.status === 'deleted');

  return (
    <>
      {(hasMultipleActive || hasAnyDeleted) && (
        <div className="mb-2 d-flex flex-wrap gap-3">
          {hasMultipleActive && (
            <Form.Check
              type="switch"
              id="show-old-calibrations"
              label={t('spray.machines.calibrations.showOldCalibrations')}
              checked={showOld}
              onChange={e => setShowOld(e.target.checked)}
            />
          )}
          {hasAnyDeleted && (
            <Form.Check
              type="switch"
              id="show-deleted-calibrations"
              label={t('spray.machines.calibrations.showDeletedCalibrations')}
              checked={showDeleted}
              onChange={e => setShowDeleted(e.target.checked)}
            />
          )}
        </div>
      )}
    <Table hover responsive size="sm">
      <thead>
        <tr>
          <th>{t('spray.machines.calibrations.date')}</th>
          <th>{t('spray.machines.calibrations.performedBy')}</th>
          <th>{t('spray.machines.calibrations.intensityLevels')}</th>
          <th>{t('spray.machines.calibrations.attachmentKey')}</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {visible.map((c, idx) => {
          const calId = c._id ?? `idx-${idx}`;
          const hasPdf = !!c.attachmentKey;
          const busy = busyId === calId;
          const isDeleted = c.status === 'deleted';
          const lastEdit = c.editHistory && c.editHistory.length > 0
            ? c.editHistory[c.editHistory.length - 1]
            : undefined;
          return (
            <tr key={calId} className={isDeleted ? 'text-muted bg-light' : undefined}>
              <td>
                <div>{formatDate(c.date, { timeZone: 'UTC' })}</div>
                {lastEdit && (
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 small text-muted"
                    onClick={() => setHistoryFor(c)}
                  >
                    {t('spray.machines.calibrations.lastEditedBy', {
                      name: lastEdit.editedBy,
                      date: formatDate(lastEdit.editedAt),
                    })}
                  </button>
                )}
              </td>
              <td>{c.performedBy}</td>
              <td>
                <div className="d-flex flex-column align-items-start gap-1">
                  {c.intensityLevels.map((lvl, i) => (
                    <span key={i} className="badge bg-secondary">
                      {formatIntensity(lvl)}
                    </span>
                  ))}
                </div>
              </td>
              <td className="text-nowrap">
                {isDeleted ? (
                  <Badge bg="secondary">
                    {t('spray.machines.calibrations.deletedPill')}
                  </Badge>
                ) : hasPdf ? (
                  <div className="d-flex gap-1 align-items-center">
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => handleView(calId)}
                      disabled={busy}
                    >
                      {t('spray.machines.calibrations.viewSignedDoc')}
                    </Button>
                    <label
                      className="btn btn-sm btn-outline-secondary mb-0"
                      htmlFor={`cal-replace-${calId}`}
                    >
                      {t('spray.machines.calibrations.replaceSignedDoc')}
                    </label>
                    <input
                      id={`cal-replace-${calId}`}
                      type="file"
                      accept="application/pdf"
                      hidden
                      aria-label={t('spray.machines.calibrations.replaceSignedDoc')}
                      onChange={e => {
                        const f = e.target.files?.[0];
                        if (f) handleReplace(calId, f);
                        e.target.value = '';
                      }}
                    />
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => handleDeletePdf(calId)}
                      disabled={busy}
                    >
                      {t('spray.machines.calibrations.deleteSignedDoc')}
                    </Button>
                  </div>
                ) : (
                  <div className="d-flex gap-2 align-items-center">
                    <Badge bg="warning" text="dark">
                      {t('spray.machines.calibrations.awaitingSignedDocument')}
                    </Badge>
                    <label
                      className="btn btn-sm btn-outline-primary mb-0"
                      htmlFor={`cal-attach-${calId}`}
                    >
                      {t('spray.machines.calibrations.attachSignedDoc')}
                    </label>
                    <input
                      id={`cal-attach-${calId}`}
                      type="file"
                      accept="application/pdf"
                      hidden
                      aria-label={t('spray.machines.calibrations.attachSignedDoc')}
                      onChange={e => {
                        const f = e.target.files?.[0];
                        if (f) handleAttach(calId, f);
                        e.target.value = '';
                      }}
                    />
                  </div>
                )}
              </td>
              <td className="text-nowrap">
                {isDeleted ? (
                  <Button
                    size="sm"
                    variant="outline-success"
                    onClick={() => handleRestore(c)}
                    disabled={busy}
                  >
                    {t('spray.machines.calibrations.restoreCalibration')}
                  </Button>
                ) : (
                  <div className="d-flex gap-1">
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => onEdit?.(c)}
                      disabled={busy || !onEdit}
                    >
                      {t('spray.machines.calibrations.editCalibration')}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => onClone?.(c)}
                      disabled={busy || !onClone}
                    >
                      {t('spray.machines.calibrations.cloneCalibration')}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => handleSoftDelete(calId)}
                      disabled={busy}
                    >
                      {t('spray.machines.calibrations.deleteCalibration')}
                    </Button>
                  </div>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
      {historyFor && (
        <CalibrationHistoryModal
          calibration={historyFor}
          onClose={() => setHistoryFor(null)}
        />
      )}
    </Table>
    </>
  );
};

export default CalibrationList;
